import React, { useState } from 'react'
import { ActionIcon, Button, Menu, Tooltip } from '@mantine/core'
import { IconDownload, IconFileTypeCsv, IconFileTypePdf, IconFileTypeXls } from '@tabler/icons-react'
import apiClient from 'axios';
import fileDownload from 'js-file-download';
import { ReportColumn, ReportConfig } from './types';
import { useSession } from '../../hooks/redux';
import { PDFOptionsModal } from './Modals/PDFOptionsModal/PDFOptionsModal';
import { DownloadRequestBody, PDFOptionsForm, ReportDownloadFileType } from '../../lib/types/ReportDownload.type';
import { notifications } from '@mantine/notifications';
import { isJsonParsable } from '../../lib/utils';

type DownloadReportProps = {
  reportPk: string | number
  columns: ReportColumn[]
  config: ReportConfig
}

export const DownloadReport = ({ reportPk, columns, config }: DownloadReportProps) => {
  const [downloadPending, setDownloadPending] = useState<{ fileType?: ReportDownloadFileType, pending: boolean }>({ fileType: undefined, pending: false })
  const [pdfOptionsModalOpen, setPdfOptionsModalOpen] = useState(false)
  const { category_pk, params } = config
  const session = useSession()
  const { csrf_token, csrf_timestamp, subsite } = session

  const pollDownload = async (url: string, fileName: string, fileType: ReportDownloadFileType, interval: number): Promise<boolean | void> => {
    try {
      const pollStatus = await apiClient.get(url)

      if (pollStatus.data.ready === true) {
        const downloadReportResponse = await apiClient.get(
          `/${subsite}/api/generic-utils/generic-download/downloadGeneric?pretty_name=${fileName}&file_name=${fileName}.${fileType}&download_type=${fileType}&csrf_token=${csrf_token}&csrf_timestamp=${csrf_timestamp}`,
          { responseType: 'arraybuffer' }
        )

        setDownloadPending({ fileType: undefined, pending: false })
        if (pdfOptionsModalOpen) setPdfOptionsModalOpen(false)
        // if response is in json format, download failed
        const decoder = new TextDecoder('utf-8')
        const decoded = decoder.decode(downloadReportResponse.data);
        if (!isJsonParsable(decoded)) {
          notifications.show({ title: 'Download Complete', message: `${fileName}.${fileType} download completed successfully.`})
          fileDownload(downloadReportResponse.data, `${fileName}.${fileType}`)
          return true
        } else {
          throw new Error(`Error downloading ${fileName} `)
        }

      } else {
        setTimeout(() => pollDownload(url, fileName, fileType, interval), interval)
        }
    } catch (error) {
      console.error('Error downloading report: ', error);
      setDownloadPending({ fileType: undefined, pending: false })
      notifications.show({ color: 'red', title: 'Download Failed', message: `There was an error downloading ${fileName}.${fileType}.`})
    }
  }


  const handleDownload = async (fileType: ReportDownloadFileType, pdfOptions?: { optionsJson: PDFOptionsForm, optionsString: string }) => {
    try {
      setDownloadPending({ fileType, pending: true })
      const reportName = params.report_name
      const fileName = reportName.split(' ').join('_')

      const body: DownloadRequestBody = {
        report_pk: reportPk,
        columns: JSON.stringify(columns),
        category_pk,
        benchmark_years: !!params.metadata?.benchmark_years?.length ? JSON.stringify(params.metadata.benchmark_years) : JSON.stringify(["current_year"]),
        reportName,
        downloadFileName: fileName,
        headerFilters: JSON.stringify([]),
        filters: JSON.stringify(config.filters),
        params: JSON.stringify(params),
        salary_display_code: params.metadata?.salary_display_code ?? 'annual',
        model_id: params.model_id ?? '',
        matrix_id: params.matrix_id ?? '',
        target_currency: '',
        contextual_filters: JSON.stringify([]),
        downloadMode: fileType,
        dynamic_filter_value: '',
        csrf_token,
        csrf_timestamp
      }

      if (pdfOptions) {
        body.pdf_options = JSON.stringify(pdfOptions.optionsJson)
        body.pdf_options_string = pdfOptions.optionsString
      }

      const urlSearchParams = new URLSearchParams(body as any);
      const encodedData = urlSearchParams.toString();

      const response = await apiClient.post(`/${subsite}/api/generic-grid/query/downloadResults`, encodedData, { headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' }});
      if (!!response.data?.pollUrl) {
        await pollDownload(response.data.pollUrl, response.data.resultFileName, fileType, 1000)
      }
    } catch (error) {
      console.error(`Error downloading ${fileType}: ${error}`)
      setDownloadPending({ fileType: undefined, pending: false })
      notifications.show({ color: 'red', title: 'Download Failed', message: `There was an error downloading report.`})
    }
  }

  return (
    <>
      <PDFOptionsModal opened={pdfOptionsModalOpen} onClose={() => setPdfOptionsModalOpen(false)} downloadPending={downloadPending.pending && downloadPending.fileType === 'pdf'} onSubmit={handleDownload} />
      <Tooltip label="Download Report">
          <ActionIcon size='xl'  variant="outline" loading={downloadPending.pending && downloadPending.fileType && ['csv', 'xlsx'].includes(downloadPending.fileType)}>
              <Menu shadow="md" width={275}>
                <Menu.Target>
                  <IconDownload style={{ width: '70%', height: '70%' }} stroke={1.5} />
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => handleDownload('csv')}>
                    <Button w='100%' variant='transparent' rightSection={<IconFileTypeCsv style={{ width: '70%', height: '70%' }} stroke={1.5} />}>
                      Download as CSV
                    </Button>
                  </Menu.Item>
                  <Menu.Item onClick={() => handleDownload('xlsx')}>
                    <Button w='100%' variant='transparent' rightSection={<IconFileTypeXls style={{ width: '70%', height: '70%' }} stroke={1.5} />}>
                      Download as Excel
                    </Button>
                  </Menu.Item>
                  <Menu.Item onClick={() => setPdfOptionsModalOpen(true)}>
                    <Button w='100%' variant='transparent' rightSection={<IconFileTypePdf style={{ width: '70%', height: '70%' }} stroke={1.5} />}>
                      Download as PDF
                    </Button>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
          </ActionIcon>
      </Tooltip>
    </>
  )
}
